import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";

import { getLocalStorageValue, setLocalStorageValue } from "utils/localStorage";

const baseUrl = process.env.REACT_APP_API_URL;

// Type T refers to the type of the data in response
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ApiResponse<T = any> = AxiosResponse<T>;

export default class ApiService {
  public static authTokenKey = "accessToken";
  public static refreshTokenKey = "refreshToken";

  private static axiosInstance: AxiosInstance;

  private static initializeAxiosInstance(): void {
    if (!this.axiosInstance) {
      this.axiosInstance = axios.create({
        baseURL: baseUrl,
      });

      this.axiosInstance.interceptors.response.use(
        (response) => response, // Pass successful responses
        async (error) => {
          const originalRequest = error.config;

          if (
            error.response.status === 401 &&
            !originalRequest._retry // Avoid infinite retry loops
          ) {
            originalRequest._retry = true; // Mark the request as retried

            const refreshToken = getLocalStorageValue(this.refreshTokenKey);

            if (refreshToken) {
              try {
                // Request a new access token using the refresh token
                const refreshResponse = await axios.post(
                  `${baseUrl}/auth/refresh`,
                  {},
                  {
                    headers: {
                      Authorization: `Bearer ${refreshToken}`,
                    },
                  },
                );

                const newAccessToken = refreshResponse.data.access_token;
                setLocalStorageValue(this.authTokenKey, newAccessToken);

                // Update the original request with the new token and retry
                originalRequest.headers["Authorization"] =
                  `Bearer ${newAccessToken}`;
                return this.axiosInstance(originalRequest);
              } catch (refreshError) {
                console.error("Failed to refresh token", refreshError);
                // Handle refresh token failure (e.g., redirect to login)
                return Promise.reject(refreshError);
              }
            } else {
              console.error("No refresh token available");
              // Handle missing refresh token (e.g., redirect to login)
            }
          }

          return Promise.reject(error); // Reject the error if no retry occurred
        },
      );
    }
  }

  public static async request(
    requestConfig: AxiosRequestConfig,
    isAuth = true,
    contentType = "application/json",
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Promise<any> {
    this.initializeAxiosInstance();

    const accessToken: string | null =
      getLocalStorageValue(this.authTokenKey) ?? null;
    if (isAuth && !accessToken) {
      return Promise.reject({ data: { message: "Not authenticated" } });
    }
    try {
      const config: AxiosRequestConfig = {
        ...requestConfig,
        baseURL: baseUrl,
        headers: {
          Authorization: accessToken ? `Bearer ${accessToken}` : null,
          "Content-Type": contentType,
          ...requestConfig.headers,
        },
      };
      const response: ApiResponse = await this.axiosInstance(config);
      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      return Promise.reject(error.response);
    }
  }
}
